import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import axios from 'axios';
import config from '../../config.js';

import '../../styles/global.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader.js';
import PageTable from '../../components/general/pageTable';
import Filter from '../../components/general/filters.js';
import DateRangePicker from '../../components/general/dateRangePicker.js';
import CreateVisitorLogForm from '../../components/forms/visitorLogForm.js';
import DeleteConfirmation from "../../components/general/deleteConfirmation";
import Loading from '../../components/general/loading.js';
import useAuthenticate from '../../components/authentication/authenticate.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const MVisitors = () => {
    const queryClient = useQueryClient();

    const [selectedProperty, setSelectedProperty] = useState(null);
    const [addOverlayOpen, setAddOverlayOpen] = useState(false);
    const [editOverlayOpen, setEditOverlayOpen] = useState(false);
    const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    // Filtered Visitors
    const [unitFilter, setUnitFilter] = useState(null);
    const [checkInFilter, setCheckInFilter] = useState(null);
    const [checkOutFilter, setCheckOutFilter] = useState(null);
    const [filterPanelVisible, setFilterPanelVisible] = useState(false);
    const [filteredVisitors, setFilteredVisitors] = useState([]);

    // Pagination
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;

    const { isLoading } = useAuthenticate();

    const { data: properties } = useQuery({
        queryKey: ['v2-properties'],
        queryFn: fetchProperties,
    });

    const { data: units = [] } = useQuery({
        queryKey: ['v2-units', selectedProperty?.value],
        queryFn: () => fetchUnitsByProperty(selectedProperty.value),
        enabled: !!selectedProperty,
    });

    const { data: visitorsData = {}, isLoading: isTableLoading } = useQuery({
        queryKey: ['v2-visitors', selectedProperty?.value, page, rowsPerPage],
        queryFn: () => fetchVisitorsByProperty(selectedProperty?.value, page, rowsPerPage),
        enabled: !!selectedProperty,
        keepPreviousData: true,
    });

    const visitors = useMemo(() => visitorsData?.results || [], [visitorsData?.results]);
    const totalPages = visitorsData?.totalPages || 1;

    useEffect(() => {
        if (properties?.length > 0) {
            setSelectedProperty((prev) => {
                if (!prev || prev.value !== properties[0].value) {
                    return properties[0];
                }
                return prev;
            });
        }
    }, [properties]);

    useEffect(() => {
        if (!visitors || visitors.length === 0) {
            setFilteredVisitors((prev) => (prev.length > 0 ? [] : prev));
            setSelectedLog((prev) => (prev !== null ? null : prev));
            return;
        }

        let filtered = [...visitors];

        if (unitFilter) {
            filtered = filtered.filter((visitor) =>
                visitor.unit._id === unitFilter.value);
        }

        if (checkInFilter) {
            const startDate = checkInFilter.startOf('day').toDate().getTime();
            filtered = filtered.filter((visitor) => {
                const checkInDate = new Date(visitor.checkIn).getTime();
                return checkInDate >= startDate;
            });
        }

        if (checkOutFilter) {
            const endDate = checkOutFilter.endOf('day').toDate().getTime();
            filtered = filtered.filter((visitor) => {
                if (!visitor.checkOut) return true;
                const checkOutDate = new Date(visitor.checkOut).getTime();
                return checkOutDate <= endDate;
            });
        }

        setFilteredVisitors(filtered);
    }, [visitors, unitFilter, checkInFilter, checkOutFilter]);

    const handleEdit = (log) => {
        setSelectedLog(log);
        setEditOverlayOpen(true);
    };

    const handleDelete = (log) => {
        setSelectedLog(log);
        setDeleteOverlayOpen(true);
    };

    const handleCheckOut = async (logId) => {
        try {
            const response = await axios.patch(
                `${config.API_URL}/v2/visitor/${logId}/checkout`,
                { checkOut: new Date().toISOString() },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                    },
                }
            );

            queryClient.setQueryData(['v2-visitors', selectedProperty?.value], (old = []) =>
                old.map((log) =>
                    log._id === logId ? { ...log, checkOut: response.data.checkOut } : log
                )
            );

            toast.success('Visitor checked out successfully!');
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error('Failed to check out the visitor.');
        }
    };

    const handleVisitorCreated = (newVisitor) => {
        queryClient.invalidateQueries(['visitors', selectedProperty?.value]);
        setAddOverlayOpen(false);
    };

    const handleVisitorUpdated = (updatedVisitor) => {
        queryClient.setQueryData(['visitors', selectedProperty?.value], (old = []) =>
            old.map((v) => v._id === updatedVisitor._id ? updatedVisitor : v)
        );

        queryClient.invalidateQueries(['visitors', selectedProperty?.value], { refetchInactive: true });

    };

    const handleVisitorDeleted = async () => {
        if (!selectedLog) return;

        try {
            await axios.delete(`${config.API_URL}/v2/visitor/${selectedLog._id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            });

            setDeleteOverlayOpen(false);
            setSelectedLog(null);
            toast.success('Visitor log deleted successfully.');
            await queryClient.invalidateQueries(['v2-visitors', selectedProperty?.value, page, rowsPerPage]);
        } catch (err) {
            console.error('Failed to delete visitor log:', err);
            toast.error('Failed to delete visitor log.');
        }
    };

    const maxVisibleButtons = 10;
    const startPage = Math.floor((page - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
    const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

    return (
        <div className="full-page w-full">
            <PageHeader pageName="Visitors" />
            {isLoading && !selectedProperty ? (
                <Loading />
            ) : (
                <>
                    <div className="page-header" style={{ alignItems: 'flex-end' }}>
                        <div className="account-dropdown">
                            <Autocomplete
                                options={properties}
                                value={selectedProperty}
                                onChange={(e, val) => {
                                    setSelectedProperty(val);
                                    setPage(1);
                                }}
                                getOptionLabel={(option) => option.label}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Property" variant="outlined" fullWidth />
                                )}
                                sx={{ width: '300px' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', height: '50px' }}>
                            <button
                                className={`filter-button ${filterPanelVisible ? 'active' : ''}`}
                                onClick={() => setFilterPanelVisible((prev) => !prev)}
                            >
                                <i className="fas fa-filter"></i> Filter
                            </button>

                            <button className="header-button" onClick={(e) => { setAddOverlayOpen(true); }}>
                                <i className="fas fa-add"></i>Visitor
                            </button>
                        </div>
                    </div>
                    {filterPanelVisible && (
                        <div className="filter-container">
                            <Filter
                                placeholder="Unit"
                                options={units}
                                value={unitFilter}
                                onChange={setUnitFilter}
                            />
                            <div style={{ width: '100%' }}>
                                <DateRangePicker
                                    startLabel="Check-In"
                                    endLabel="Check-Out"
                                    startDate={checkInFilter}
                                    endDate={checkOutFilter}
                                    onStartDateChange={setCheckInFilter}
                                    onEndDateChange={setCheckOutFilter}
                                />
                            </div>
                        </div>
                    )}
                    {isTableLoading ? (
                        <Loading />
                    ) : (
                        <div className="w-full table-wrapper">
                            <PageTable
                                rows={filteredVisitors}
                                columns={[
                                    { label: 'Unit', accessor: (row) => row.unit?.unitNumber, width: '10%' },
                                    { label: 'First Name', accessor: 'givenName', width: '20%' },
                                    { label: 'Last Name', accessor: 'lastName', width: '20%' },
                                    { label: 'Mobile No.', accessor: 'mobile', width: '15%' },
                                    { label: 'Email', accessor: 'email', width: '20%' },
                                    { label: 'Purpose', accessor: 'purpose', width: '20%' },
                                    { label: 'Check-In', accessor: (row) => new Date(row.checkIn).toLocaleDateString(), width: '15%' },
                                    { label: 'Check-Out', accessor: (row) => row.checkOut ? new Date(row.checkOut).toLocaleDateString() : '—', width: '15%' },
                                ]}
                                hasButtons={true}
                                hasDelete={true}
                                hasCheckout={true}
                                hasEdit={true}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onCheckout={(row) => handleCheckOut(row._id)}
                            />

                            {totalPages > 1 && (
                                <div className="pagination-controls">
                                    <div className="pagination-buttons">
                                        <button
                                            className="pagination-button"
                                            disabled={page === 1}
                                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>

                                        {startPage > 1 && (
                                            <button className="pagination-page" onClick={() => setPage(startPage - 1)} >
                                                ...
                                            </button>
                                        )}

                                        {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                                            const pageNum = startPage + i;
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`pagination-page ${page === pageNum ? 'active' : ''}`}
                                                    onClick={() => setPage(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        {endPage < totalPages && (
                                            <button className="pagination-page" onClick={() => setPage(endPage + 1)} >
                                                ...
                                            </button>
                                        )}

                                        <button
                                            className="pagination-button"
                                            disabled={page === totalPages}
                                            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
            {addOverlayOpen && (
                <CreateVisitorLogForm
                    propertyId={selectedProperty?.value}
                    units={units}
                    onVisitorLogCreated={handleVisitorCreated}
                    onClose={() => setAddOverlayOpen(false)}
                />
            )}
            {editOverlayOpen && (
                <CreateVisitorLogForm
                    propertyId={selectedProperty?.value}
                    units={units}
                    selectedLog={selectedLog}
                    onVisitorLogUpdated={handleVisitorUpdated}
                    onClose={() => setEditOverlayOpen(false)}
                />
            )}
            {deleteOverlayOpen && (
                <DeleteConfirmation
                    onConfirm={handleVisitorDeleted}
                    setIsOverlayOpen={setDeleteOverlayOpen}
                />
            )}
        </div>
    );
};

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    console.log("Properties: ", response.data)
    return response.data.map(item => ({
        value: item._id,
        label: item.name,
    }));
}

const fetchUnitsByProperty = async (propertyId) => {
    const response = await axios.get(`${config.API_URL}/v2/unit/property/${propertyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    return response.data.map(item => ({
        value: item._id,
        label: item.unitNumber,
    }));
};

const fetchVisitorsByProperty = async (propertyId, page = 1, limit = 10) => {
    if (!propertyId) return { results: [], totalPages: 0 };
    try {
        const response = await axios.get(`${config.API_URL}/v2/visitor/query/property/${propertyId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            params: { page, limit, sortBy: 'createdAt', sortOrder: 'desc' },
        });
        console.log('Visitors (paginated):', response.data);
        return response.data;
    } catch (err) {
        console.error('Error fetching paginated visitors:', err);
        return { results: [], totalPages: 0 };
    }
};

export default MVisitors;
